import { isBrowser } from '@dx-ui/utilities-is-browser';
import type { RumInternalContext } from '@datadog/browser-rum';

export type DatadogFailureReason = 'error' | 'abandoned' | 'other';

export interface DatadogOperationOptions {
  operationKey?: string;
  context?: Record<string, unknown>;
  description?: string;
}

/**
 * Datadog RUM API types
 * Based on @datadog/browser-rum types
 */
export interface DatadogRumApi {
  /**
   * Get the internal SDK context for the current RUM session.
   * This can be unavailable while the CDN SDK is still loading.
   */
  getInternalContext?: (startTime?: number) => RumInternalContext | undefined;

  /**
   * Set the user information
   * @param user - User information object
   */
  setUser: (user: { id?: string; name?: string; email?: string; [key: string]: unknown }) => void;

  /**
   * Set a custom attribute
   * @param key - Attribute key
   * @param value - Attribute value
   */
  setGlobalContextProperty: (key: string, value: unknown) => void;

  /**
   * Remove a custom attribute
   * @param key - Attribute key
   */
  removeGlobalContextProperty: (key: string) => void;

  /**
   * Add a custom action
   * @param name - Action name
   * @param context - Optional context object
   */
  addAction: (name: string, context?: Record<string, unknown>) => void;

  /**
   * Add a custom error
   * @param error - Error object or message
   * @param context - Optional context object
   */
  addError: (error: Error | string, context?: Record<string, unknown>) => void;

  /**
   * Start a custom view
   * @param name - View name
   * @param context - Optional context object
   */
  startView: (name: string, context?: Record<string, unknown>) => void;

  /**
   * Stop the current view
   */
  stopView: () => void;

  /**
   * Set the view name
   * @param name - View name
   */
  setViewName: (name: string) => void;

  /**
   * Add a feature flag evaluation
   * @param key - Feature flag key/name
   * @param value - Feature flag value
   */
  addFeatureFlagEvaluation: (key: string, value: boolean | string | number) => void;

  /**
   * Start an operation for operations monitoring.
   * @param name - Operation name
   * @param options - Optional operationKey, context, and description
   */
  startOperation?: (name: string, options?: DatadogOperationOptions) => void;

  /**
   * Legacy Datadog operations monitoring API. Prefer {@link startOperation}.
   */
  startFeatureOperation?: (name: string, options?: DatadogOperationOptions) => void;

  /**
   * Mark an operation as successful.
   * @param name - Operation name
   * @param options - Optional operationKey, context, and description
   */
  succeedOperation?: (name: string, options?: DatadogOperationOptions) => void;

  /**
   * Legacy Datadog operations monitoring API. Prefer {@link succeedOperation}.
   */
  succeedFeatureOperation?: (name: string, options?: DatadogOperationOptions) => void;

  /**
   * Mark an operation as failed.
   * @param name - Operation name
   * @param failureReason - Reason for the failure
   * @param options - Optional operationKey, context, and description
   */
  failOperation?: (
    name: string,
    failureReason: DatadogFailureReason,
    options?: DatadogOperationOptions
  ) => void;

  /**
   * Legacy Datadog operations monitoring API. Prefer {@link failOperation}.
   */
  failFeatureOperation?: (
    name: string,
    failureReason: DatadogFailureReason,
    options?: DatadogOperationOptions
  ) => void;
}

declare global {
  interface Window {
    DD_RUM?: {
      init: (config: Record<string, unknown>) => void;
      onReady: (callback: () => void) => void;
      setGlobalContextProperty: (key: string, value: unknown) => void;
    } & DatadogRumApi;
    DD_LOGS?: {
      init: (config: Record<string, unknown>) => void;
      onReady: (callback: () => void) => void;
      createLogger: (name: string, config?: Record<string, unknown>) => void;
      getLogger: (name: string) =>
        | {
            debug: (message: string, context?: Record<string, unknown>) => void;
            info: (message: string, context?: Record<string, unknown>) => void;
            warn: (message: string, context?: Record<string, unknown>) => void;
            error: (message: string, context?: Record<string, unknown>) => void;
          }
        | undefined;
      logger: {
        debug: (message: string, context?: Record<string, unknown>) => void;
        info: (message: string, context?: Record<string, unknown>) => void;
        warn: (message: string, context?: Record<string, unknown>) => void;
        error: (message: string, context?: Record<string, unknown>) => void;
      };
    };
  }
}

/**
 * Simple wrapper around the Datadog RUM API.
 * Returns undefined if not available (e.g., on server-side or when not initialized).
 *
 * @example
 * ```ts
 * import { datadogRum } from '@dx-ui/framework-datadog-rum';
 *
 * if (datadogRum) {
 *   datadogRum.setUser({ id: '123', name: 'John Doe' });
 *   datadogRum.addAction('button_click', { buttonName: 'submit' });
 * }
 * ```
 */
export const datadogRum: DatadogRumApi | undefined = isBrowser ? window.DD_RUM : undefined;

/**
 * Return the active Datadog RUM session ID when the browser SDK has one.
 */
export function getDatadogRumSessionId(): string | undefined {
  const sessionId = isBrowser ? window.DD_RUM?.getInternalContext?.()?.session_id : undefined;
  return sessionId || undefined;
}
